import { Alert, SpaceBetween, Spinner, StatusIndicator, TextContent } from '@amzn/awsui-components-react-v3';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { ValidateFormDataWithD3a } from 'src/components/datausageagreement/validateFormDataWithD3a';
import { DataAgreement, ValidateContextForDataAgreementOutput, Validation } from 'aws-sdk/clients/d3aservice';
import { getDataAgreement, validateContextForDataAgreement } from 'src/api/d3a';
import { ReviewReviewerList } from 'aws-sdk/clients/awsdlomni';
import { FORM_CONTEXTS_FOR_REVIEWS } from 'src/components/datausageagreement/utils';

export interface ReviewAgreementProps {
  setContentType: React.Dispatch<React.SetStateAction<string>>;
  match: any;
  agreementId: string;
  agreementData: object;
  setAgreementData: React.Dispatch<React.SetStateAction<object>>;
  reviews: ReviewReviewerList;
  overrideReason: string;
  setOverrideReason: React.Dispatch<React.SetStateAction<string>>;
  header?: ReactNode;
  disabled?: boolean;
  d3AValidationOutput: ValidateContextForDataAgreementOutput;
}

export const ReviewAgreement = (props: ReviewAgreementProps) => {
  const [dataAgreement, setDataAgreement] = useState<DataAgreement>(undefined);
  const [errors, setErrors] = useState([]);

  // Validation state — initialized from pre-computed d3AValidationOutput prop
  const [isValid, setIsValid] = useState<boolean>(props.d3AValidationOutput?.isValid ?? false);
  const [isUnknown, setIsUnknown] = useState<boolean>(!props.d3AValidationOutput);
  const [validationList, setValidationList] = useState<Validation[]>(props.d3AValidationOutput?.validationList ?? []);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>(undefined);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  // Sync validation state when prop changes
  useEffect(() => {
    if (props.d3AValidationOutput) {
      setIsValid(props.d3AValidationOutput.isValid);
      setIsUnknown(false);
      setValidationList(props.d3AValidationOutput.validationList ?? []);
    }
  }, [props.d3AValidationOutput]);

  const handleRefresh = async () => {
    try {
      const getDataAgreementOutput = await getDataAgreement({ id: props.agreementId });
      setDataAgreement(getDataAgreementOutput.dataAgreements[0]);
    } catch (err) {
      console.error(err);
    }
  };

  const validate = async () => {
    setValidationLoading(true);
    try {
      const output: ValidateContextForDataAgreementOutput = await validateContextForDataAgreement({
        id: dataAgreement.id,
        versionId: dataAgreement.versionId,
        context: props.agreementData,
        validationContexts: FORM_CONTEXTS_FOR_REVIEWS,
      });
      setIsValid(output.isValid);
      setValidationList(output.validationList);
      setValidationErrorMessage(undefined);
    } catch (e) {
      console.error('Error when validating form inputs: ', e);
      setIsValid(false);
      setValidationList([]);
      setValidationErrorMessage(`Internal error when validating: ${e.message}`);
    }
    setValidationLoading(false);
    setIsUnknown(false);
  };

  /** Renders non-D3A validation errors (schema-level: required, minLength, etc.) prominently. */
  const nonD3aErrors = (validationList) => {
    const failures = validationList
      .filter((v) => !v.d3aValidationTypes || v.d3aValidationTypes.length === 0)
      .flatMap((v) => {
        const entries = Object.entries(v.validationResult as object);
        return entries.map(([_, val]) => (
          <li key={`${v.attribute}-${_}`}>
            <StatusIndicator type={'error'}>
              <b>{v.attribute}: </b>
              {val?.reason}
            </StatusIndicator>
          </li>
        ));
      });

    if (failures.length === 0) return null;

    return (
      <Alert type={'error'} header={'Required field errors (must be fixed before proceeding)'}>
        <TextContent>
          <ul>{failures}</ul>
        </TextContent>
      </Alert>
    );
  };

  return (
    <>
      {dataAgreement ? (
        <SpaceBetween size={'m'} direction={'vertical'}>
          <UsageAgreementForm
            header={props.header}
            schema={JSON.stringify(dataAgreement?.jsonSchema ?? '{}')}
            formData={props.agreementData}
            setFormData={props.setAgreementData}
            errors={errors}
            setErrors={setErrors}
            readOnly={false}
            contexts={FORM_CONTEXTS_FOR_REVIEWS}
          />
          {!isUnknown && !isValid && nonD3aErrors(validationList)}
          {!isUnknown && !isValid && (
            <ValidateFormDataWithD3a
              readonly={false}
              loading={validationLoading}
              formData={props.agreementData}
              isValid={isValid}
              isUnknown={isUnknown}
              validationList={validationList.filter((v) => v.d3aValidationTypes?.length > 0)}
              validationErrorMessage={validationErrorMessage}
              validate={validate}
              overrideReason={props.overrideReason}
              setOverrideReason={props.setOverrideReason}
            />
          )}
        </SpaceBetween>
      ) : (
        <Spinner size={'large'} />
      )}
    </>
  );
};
